import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Highcharts from 'highcharts/esm/highcharts';
import 'highcharts/esm/highcharts-more';
import 'highcharts/esm/modules/accessibility';
import 'highcharts/esm/themes/adaptive';
import {
  HighchartsReact,
  HighchartsReactRefObject,
} from 'highcharts-react-official';
import cx from 'classnames';
import { Maximize2, Minimize2 } from 'lucide-react';
import { useRedraw } from './useRedraw';

export interface ResponsiveChartProps {
  /** Highcharts configuration options */
  options: Highcharts.Options;

  /** Enable fullscreen mode toggle button */
  expandable?: boolean;

  /** Controlled fullscreen state (optional) */
  isFullscreen?: boolean;

  /** Callback when fullscreen state changes */
  onFullscreenChange?: (isFullscreen: boolean) => void;

  /**
   * Whether this component renders the fullscreen overlay itself (portal to
   * body, fixed positioning, mobile rotation). Set false when an ancestor
   * already renders its own fullscreen wrapper around this chart — two
   * overlays fight for the same z-index and one hides the other.
   */
  fullscreenWrapper?: boolean;

  /** Additional CSS classes for the container */
  className?: string;

  /** Inline styles for the container */
  style?: React.CSSProperties;
}

/**
 * ResponsiveChart - A mobile-ready Highcharts wrapper with ResizeObserver
 *
 * Provides:
 * - Automatic chart resizing via ResizeObserver
 * - Mobile rotation and orientation change handling
 * - Fullscreen mode with portal rendering
 * - Proper reflow on container size changes
 *
 * This component handles all the complex responsive behavior so app-specific
 * Chart components can focus on data transformation and configuration.
 */
export const ResponsiveChart: React.FC<ResponsiveChartProps> = ({
  options,
  expandable = true,
  isFullscreen: isFullscreenProp,
  onFullscreenChange,
  fullscreenWrapper = true,
  className,
  style,
}) => {
  const chartComponentRef = useRef<HighchartsReactRefObject>(null);
  const [chartContainerRef, setChartContainerRef] =
    useState<HTMLDivElement | null>(null);
  const [isFullscreenInternal, setIsFullscreenInternal] = useState(false);

  const dimensions = useRef({ width: 0, height: 0 });
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Use controlled prop if provided, otherwise use internal state
  const isFullscreen =
    isFullscreenProp !== undefined ? isFullscreenProp : isFullscreenInternal;
  const isParentControlled = isFullscreenProp !== undefined;

  // Apply responsive behavior via ResizeObserver
  useRedraw(chartComponentRef, chartContainerRef);

  const handleFullscreenToggle = useCallback(() => {
    const newFullscreenState = !isFullscreen;

    // Store dimensions when entering fullscreen
    if (newFullscreenState && chartContainerRef) {
      dimensions.current.width = chartContainerRef.clientWidth;
      dimensions.current.height = chartContainerRef.clientHeight;
    }

    // Restore dimensions when exiting fullscreen
    if (!newFullscreenState && chartContainerRef) {
      chartContainerRef.style.width = `${dimensions.current.width}px`;
      chartContainerRef.style.height = `${dimensions.current.height}px`;
    }

    // Update state (controlled or internal)
    if (isParentControlled && onFullscreenChange) {
      onFullscreenChange(newFullscreenState);
    } else {
      setIsFullscreenInternal(newFullscreenState);
    }

    // Manage body overflow
    if (newFullscreenState) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    // Force chart resize and redraw after state change
    if (chartComponentRef.current?.chart) {
      setTimeout(() => {
        const chart = chartComponentRef.current?.chart;
        if (chart) {
          chart.reflow();
          chart.redraw(true);
        }
      }, 100);
    }
  }, [isFullscreen, chartContainerRef, isParentControlled, onFullscreenChange]);

  // Handle Escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        handleFullscreenToggle();
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen, handleFullscreenToggle]);

  const content = () => {
    // isFullscreen reflects the current mode regardless of who owns the
    // underlying state (this component's own useState, or a parent relaying
    // it back through onFullscreenChange as Chart.tsx does). Applying the
    // overlay styling is a separate question: `fullscreenWrapper` is false
    // when an ancestor (ChartCard) already renders the fullscreen container,
    // and then this component just fills it.
    const overlay = isFullscreen && fullscreenWrapper;

    return (
      <div
        data-component="ResponsiveChart"
        className={cx(
          'flex flex-col relative h-full w-full',
          {
            // Rotate on mobile, normal inset on desktop.
            'fixed z-[9999] bg-white dark:bg-gray-900': overlay && !isMobile,
            'inset-0': overlay && !isMobile,
          },
          className
        )}
        style={{
          ...(overlay && isMobile
            ? {
                position: 'fixed',
                width: '100vh',
                height: '100vw',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(90deg)',
                zIndex: 9999,
              }
            : {}),
          ...style,
        }}
        ref={(ref) => setChartContainerRef(ref)}
      >
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          ref={chartComponentRef}
          containerProps={{
            style: {
              flexGrow: 1,
              minHeight: 0,
            },
          }}
        />
        {expandable && isMobile && (
          <button
            className="absolute right-2 top-2 p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 transition-colors shadow-lg"
            onClick={handleFullscreenToggle}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullscreen ? (
              <Minimize2 className="w-5 h-5 text-foreground-secondary" />
            ) : (
              <Maximize2 className="w-5 h-5 text-foreground-secondary" />
            )}
          </button>
        )}
      </div>
    );
  };

  return isFullscreen && fullscreenWrapper
    ? createPortal(content(), document.body)
    : content();
};

ResponsiveChart.displayName = 'ResponsiveChart';
